import React, { useState, useEffect, useRef } from "react";
import "./Import.css";
import Header from "../header/Header";
import Footer from "../footer/Footer";
import Sidebar from "../Sidebar";
import VivahApi from "../../api";

const REQUIRED_HEADERS = ['email', 'firstName', 'lastName'];

const TEMPLATE_HEADERS = ['email', 'firstName', 'lastName', 'phone', 'gender', 'dateOfBirth', 'religion', 'caste', 'gothra', 'motherTongue'];
const TEMPLATE_SAMPLE  = ['priya@example.com', 'Priya', 'Sharma', '9876543210', 'F', '1995-06-15', 'Hindu', 'Brahmin', '', 'Hindi'];

function downloadTemplate() {
  const csv = [TEMPLATE_HEADERS.join(','), TEMPLATE_SAMPLE.join(',')].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'import-template.csv';
  a.click();
}

function normalizePhone(p) {
  if (!p) return null;
  const digits = String(p).replace(/[^0-9]/g, '');
  if (String(p).startsWith('+')) return `+${digits}`;
  if (digits.length === 10) return `+91${digits}`;
  if (digits.length === 12 && digits.startsWith('91')) return `+${digits}`;
  return null;
}

function buildLookup(items) {
  const map = {};
  (items || []).forEach(item => {
    map[String(item.id)] = item;
    map[item.label.toLowerCase()] = item;
    map[item.value.toLowerCase()] = item;
  });
  return map;
}

function resolveId(val, lookup) {
  if (!val) return { id: '', name: '' };
  const key = String(val).trim().toLowerCase();
  const found = lookup[key] || lookup[String(val).trim()];
  if (found) return { id: String(found.id), name: found.label };
  return { id: val, name: val, unknown: true };
}

function processRows(rawRows, religionLookup, casteLookup, languageLookup) {
  return rawRows.map((r, idx) => {
    const issues = [];
    const out = { ...r };

    // Phone
    const rawPhone = (r.phone || '').toString().trim();
    if (rawPhone) {
      const normalized = normalizePhone(rawPhone);
      if (!normalized) {
        issues.push(`Phone "${rawPhone}" is invalid — use 10-digit number or +91XXXXXXXXXX`);
        out._phoneError = true;
      } else {
        out.phone = normalized;
      }
    }

    // Religion name → ID
    if (r.religion !== undefined) {
      if (r.religion) {
        const resolved = resolveId(r.religion, religionLookup);
        out.religionId = resolved.id;
        if (resolved.unknown) { issues.push(`Religion "${r.religion}" not recognised — check spelling`); out._religionError = true; }
      } else {
        out.religionId = '';
      }
    }

    // Caste name → ID
    if (r.caste !== undefined) {
      if (r.caste) {
        const resolved = resolveId(r.caste, casteLookup);
        out.casteId = resolved.id;
        if (resolved.unknown) { issues.push(`Caste "${r.caste}" not recognised — check spelling`); out._casteError = true; }
      } else {
        out.casteId = '';
      }
    }

    // Gothra name → ID (uses same castes data)
    if (r.gothra !== undefined) {
      if (r.gothra) {
        const resolved = resolveId(r.gothra, casteLookup);
        out.gothraId = resolved.id;
        if (resolved.unknown) { issues.push(`Gothra "${r.gothra}" not recognised — check spelling`); out._gothraError = true; }
      } else {
        out.gothraId = '';
      }
    }

    // Mother tongue name → ID
    if (r.motherTongue !== undefined) {
      if (r.motherTongue) {
        const resolved = resolveId(r.motherTongue, languageLookup);
        out.motherTongueId = resolved.id;
        if (resolved.unknown) { issues.push(`Mother tongue "${r.motherTongue}" not recognised — check spelling`); out._motherTongueError = true; }
      } else {
        out.motherTongueId = '';
      }
    }

    // Gender normalise
    if (r.gender) {
      const g = r.gender.trim().toLowerCase();
      out.gender = (g === 'male' || g === 'm') ? 'M' : (g === 'female' || g === 'f') ? 'F' : r.gender;
    }

    out._issues = issues;
    out._rowNum = idx + 2; // 1-indexed + header row
    return out;
  });
}

export default function Import() {
  const [fileName, setFileName]       = useState(null);
  const [rawRows, setRawRows]         = useState([]);
  const [processedRows, setProcessed] = useState([]);
  const [headers, setHeaders]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [message, setMessage]         = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [religions, setReligions]       = useState({});
  const [castes, setCastes]             = useState({});
  const [languages, setLanguages]       = useState({});
  const fileRef = useRef();

  useEffect(() => {
    fetch('/master-data/religions.json').then(r => r.json()).then(d => setReligions(buildLookup(d))).catch(() => {});
    fetch('/master-data/castes.json').then(r => r.json()).then(d => setCastes(buildLookup(d))).catch(() => {});
    fetch('/master-data/languages.json').then(r => r.json()).then(d => setLanguages(buildLookup(d))).catch(() => {});
  }, []);

  function reset() {
    setRawRows([]); setProcessed([]); setHeaders([]); setFileName(null);
    setMessage(null); setImportResult(null);
    if (fileRef.current) fileRef.current.value = '';
  }

  async function handleFile(e) {
    const file = e.target.files[0];
    reset();
    if (!file) return;
    setFileName(file.name);

    const text = await file.text();
    const lines = text.split(/\r?\n/).filter(Boolean);
    if (lines.length < 2) { setMessage({ type: 'error', text: 'CSV has no data rows.' }); return; }

    const hdr = lines[0].split(',').map(h => h.trim());
    const missing = REQUIRED_HEADERS.filter(h => !hdr.includes(h));
    if (missing.length) {
      setMessage({ type: 'error', text: `Missing required columns: ${missing.join(', ')}` });
      setHeaders(hdr);
      return;
    }

    const data = lines.slice(1).map(line => {
      const vals = line.split(',').map(v => v.trim());
      const obj = {};
      hdr.forEach((h, i) => { obj[h] = vals[i] ?? ''; });
      return obj;
    }).filter(r => Object.values(r).some(v => v !== ''));

    setHeaders(hdr);
    setRawRows(data);
    const processed = processRows(data, religions, castes, languages);
    setProcessed(processed);

    const issueCount = processed.filter(r => r._issues.length > 0).length;
    if (issueCount > 0) {
      setMessage({ type: 'warning', text: `${data.length} row(s) loaded. ${issueCount} row(s) have warnings — review below before uploading.` });
    } else {
      setMessage({ type: 'info', text: `${data.length} row(s) loaded. Ready to import.` });
    }
  }

  async function handleUpload() {
    if (!processedRows.length) { setMessage({ type: 'error', text: 'No rows to import.' }); return; }
    const phoneErrors = processedRows.filter(r => r._phoneError).length;
    if (phoneErrors > 0) {
      setMessage({ type: 'error', text: `Fix ${phoneErrors} invalid phone number(s) in the CSV before uploading.` });
      return;
    }
    const fieldErrors = processedRows.filter(r => r._religionError || r._casteError || r._gothraError || r._motherTongueError).length;
    if (fieldErrors > 0) {
      setMessage({ type: 'error', text: `Fix ${fieldErrors} row(s) with unrecognised Religion/Caste/Gothra/Mother Tongue values before uploading.` });
      return;
    }

    setImportResult(null);
    setMessage({ type: 'info', text: `Importing ${processedRows.length} row(s)…` });
    setLoading(true);

    // Rebuild CSV from processed rows (names resolved to IDs, phones normalised)
    const outHeaders = [...new Set([...REQUIRED_HEADERS, ...Object.keys(processedRows[0]).filter(k => !k.startsWith('_'))])];
    const csvLines = [
      outHeaders.join(','),
      ...processedRows.map(r => outHeaders.map(h => (r[h] ?? '')).join(','))
    ];
    const csv = csvLines.join('\n');

    try {
      const resp = await VivahApi.Admin.importData(csv);
      const payload = resp?.data || {};
      const results = Array.isArray(payload.results) ? payload.results : [];

      if (results.length > 0) {
        const summary = results.reduce((acc, item) => {
          const key = (item.status || 'unknown').toLowerCase();
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        }, {});
        setImportResult({ rowCount: payload.rowCount || processedRows.length, results, summary });
        setMessage((summary.failed || 0) > 0
          ? { type: 'warning', text: `Import done with issues — ${summary.failed} row(s) failed.` }
          : { type: 'success', text: `Import complete. ${summary.created || 0} created, ${summary.skipped || 0} skipped.` }
        );
      } else {
        setMessage({ type: 'success', text: payload.message || 'Import submitted.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err?.response?.data?.error || err.message || 'Import failed.' });
    } finally {
      setLoading(false);
    }
  }

  const previewHeaders = headers.filter(h => !h.startsWith('_'));
  const hasIssues = processedRows.some(r => r._issues?.length > 0);

  return (
    <div className="gla_page" id="gla_page">
      <a href="#gla_page" className="gla_top ti ti-angle-up gla_go"></a>
      <Header />
      <div className="gla_page_title gla_image_bck gla_wht_txt" data-color="#282828">
        <div className="container text-left">
          <div className="row">
            <div className="col-md-8"><h1 className="gla_h1_title">Import Users</h1></div>
          </div>
        </div>
      </div>

      <section id="gla_content" className="gla_content">
        <section className="gla_section">
          <div className="container">
            <div className="dashboard">
              <div className="row">
                <Sidebar />
                <div className="col-md-10 col-xs-12 dashboard_container">
                  <div className="import-box">

                    {/* Format guide */}
                    <div className="import-guide">
                      <div className="import-guide-title">CSV Format Guide</div>
                      <div className="import-guide-grid">
                        <div className="import-guide-col">
                          <div className="import-guide-label">Required columns</div>
                          <code>email, firstName, lastName</code>
                        </div>
                        <div className="import-guide-col">
                          <div className="import-guide-label">Optional columns</div>
                          <code>phone, gender, dateOfBirth, religion, caste, gothra, motherTongue</code>
                        </div>
                        <div className="import-guide-col">
                          <div className="import-guide-label">Phone format</div>
                          <code>9876543210</code> <span className="import-guide-hint">or</span> <code>+919876543210</code>
                          <div className="import-guide-hint">10-digit numbers are auto-prefixed with +91</div>
                        </div>
                        <div className="import-guide-col">
                          <div className="import-guide-label">Religion / Caste / Mother Tongue</div>
                          <div className="import-guide-hint">Use names: <code>Hindu</code>, <code>Brahmin</code>, <code>Hindi</code> — IDs resolved automatically</div>
                        </div>
                        <div className="import-guide-col">
                          <div className="import-guide-label">Gender</div>
                          <code>M</code> / <code>F</code> / <code>T</code> <span className="import-guide-hint">or</span> <code>Male</code> / <code>Female</code>
                        </div>
                        <div className="import-guide-col">
                          <div className="import-guide-label">Date of Birth</div>
                          <code>YYYY-MM-DD</code> <span className="import-guide-hint">e.g. 1995-06-15</span>
                        </div>
                        <div className="import-guide-col">
                          <div className="import-guide-label">Gothra</div>
                          <div className="import-guide-hint">Only for Hindu users — leave blank for others</div>
                        </div>
                      </div>
                      <button className="import-template-btn" onClick={downloadTemplate}>
                        ↓ Download Template CSV
                      </button>
                    </div>

                    {/* Controls */}
                    <div className="import-controls">
                      <label className="import-choose-btn">
                        Choose CSV
                        <input ref={fileRef} type="file" accept=".csv" onChange={handleFile} style={{ display: 'none' }} />
                      </label>
                      {fileName && <span className="import-file-name">{fileName} · {rawRows.length} row(s)</span>}
                    </div>

                    {/* Status */}
                    {message && (
                      <div className={`import-status import-status-${message.type}`}>{message.text}</div>
                    )}

                    {/* Preview table */}
                    {processedRows.length > 0 && (
                      <div>
                        <div className="import-preview-title">
                          Preview
                          {hasIssues && <span className="import-warn-badge">⚠ {processedRows.filter(r => r._issues.length).length} row(s) with warnings</span>}
                        </div>
                        <div style={{ overflowX: 'auto' }}>
                          <table className="admin-users-table">
                            <thead>
                              <tr>
                                <th>#</th>
                                {previewHeaders.map(h => <th key={h}>{h}</th>)}
                                <th>Warnings</th>
                              </tr>
                            </thead>
                            <tbody>
                              {processedRows.slice(0, 10).map((r, idx) => (
                                <tr key={idx} className={r._issues.length ? 'import-row-warn' : ''}>
                                  <td className="import-row-num">{r._rowNum}</td>
                                  {previewHeaders.map(h => (
                                    <td key={h} className={
                                      (h === 'phone'       && r._phoneError)        ||
                                      (h === 'religion'    && r._religionError)     ||
                                      (h === 'caste'       && r._casteError)        ||
                                      (h === 'gothra'      && r._gothraError)       ||
                                      (h === 'motherTongue' && r._motherTongueError)
                                        ? 'import-cell-error' : ''
                                    }>
                                      {r[h] ?? ''}
                                    </td>
                                  ))}
                                  <td className="import-issues-cell">
                                    {r._issues.length
                                      ? r._issues.map((msg, i) => <div key={i} className="import-issue">{msg}</div>)
                                      : <span className="import-ok">✓</span>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                        {processedRows.length > 10 && (
                          <div className="import-more">…and {processedRows.length - 10} more rows</div>
                        )}
                      </div>
                    )}

                    {/* Action buttons */}
                    {rawRows.length > 0 && (
                      <div className="import-actions">
                        <button
                          className="button btn btn-default"
                          onClick={handleUpload}
                          disabled={loading}
                        >
                          {loading ? `Importing…` : `Upload ${processedRows.length} row(s)`}
                        </button>
                        <button className="button btn" onClick={reset}>Reset</button>
                      </div>
                    )}

                    {/* Results */}
                    {importResult && (
                      <div className="import-result">
                        <div className="import-result-title">Import Summary</div>
                        <div className="import-summary-chips">
                          <span className="import-chip">Total: {importResult.rowCount}</span>
                          <span className="import-chip import-chip-success">Created: {importResult.summary.created || 0}</span>
                          <span className="import-chip">Skipped: {importResult.summary.skipped || 0}</span>
                          {(importResult.summary.failed || 0) > 0 && (
                            <span className="import-chip import-chip-error">Failed: {importResult.summary.failed}</span>
                          )}
                        </div>
                        <div style={{ overflowX: 'auto', marginTop: 12 }}>
                          <table className="admin-users-table">
                            <thead>
                              <tr><th>Email</th><th>Status</th><th>Message</th></tr>
                            </thead>
                            <tbody>
                              {importResult.results.map((r, idx) => (
                                <tr key={idx} className={r.status === 'failed' || r.status === 'error' ? 'import-row-warn' : ''}>
                                  <td>{r.email || '-'}</td>
                                  <td>{r.status}</td>
                                  <td>{r.message || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </section>
      <Footer />
    </div>
  );
}
